/**
 * PDF Download Service for Jewelry Billing & Estimates
 *
 * Web approach: calls POST /api/pdf/estimate or /api/pdf/invoice,
 * receives a binary PDF from the server (pdfkit), and triggers a
 * browser download. Works on Pi, Azure, and everywhere.
 */

const API_BASE = process.env.REACT_APP_API_URL || '/api';

class PdfService {
  /**
   * Download an estimate as PDF.
   * @param {Object} estimateData - All estimate fields
   * @param {string} currency - 'INR' | 'USD'
   */
  static async downloadEstimatePDF(estimateData, currency = 'INR') {
    return PdfService._download('/pdf/estimate', { ...estimateData, currency }, 'estimate');
  }

  static async getEstimatePdfUrl(estimateData, currency = 'INR') {
    return PdfService._pdfUrl('/pdf/estimate', { ...estimateData, currency });
  }

  /**
   * Download a Kundan estimate as a narrow mobile-sized PDF.
   * @param {Object} data - Kundan estimate fields
   */
  static async downloadKundanEstimatePDF(data) {
    return PdfService._download('/pdf/kundan-estimate', data, 'kundan_estimate');
  }

  static async getKundanEstimatePdfUrl(data) {
    return PdfService._pdfUrl('/pdf/kundan-estimate', data);
  }

  /**
   * Download an invoice (billing) as PDF.
   * @param {Object} invoiceData - All invoice fields
   * @param {string} currency - 'INR' | 'USD'
   */
  static async downloadInvoicePDF(invoiceData, currency = 'INR') {
    return PdfService._download('/pdf/invoice', { ...invoiceData, currency }, 'invoice');
  }

  /**
   * Download a multi-item invoice (one bill, several item lines) as PDF.
   * @param {Object} invoiceData - Header fields + items[] array
   * @param {string} currency - 'INR' | 'USD'
   */
  static async downloadMultiItemInvoicePDF(invoiceData, currency = 'INR') {
    return PdfService._download('/pdf/invoice-multi', { ...invoiceData, currency }, 'invoice');
  }

  static async getMultiItemInvoicePdfUrl(invoiceData, currency = 'INR') {
    return PdfService._pdfUrl('/pdf/invoice-multi', { ...invoiceData, currency });
  }

  // ── Internal ──────────────────────────────────────────
  // POST the payload and return the PDF as a viewable object URL (shared by get*PdfUrl).
  static async _pdfUrl(endpoint, payload) {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Server returned ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  static async _download(endpoint, payload, docType) {
    try {
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server returned ${response.status}`);
      }

      // Trigger browser download
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const customerSlug = (payload.customerName || 'customer')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_]/g, '')
        .toLowerCase();
      a.href = url;
      a.download = `${docType}_${customerSlug}_${Date.now()}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      console.error(`PDF ${docType} download error:`, error);
      alert(`Failed to download PDF.\n\nError: ${error.message}\n\nMake sure the backend server is running.`);
      return false;
    }
  }
}

export default PdfService;
